import { get, sumBy } from 'lodash';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { useGetProduct } from 'hooks/useGetProduct/useGetProduct';
import { OrderItem } from 'hooks/useGetProduct/types';
import { pushToDataLayer } from '../Analytics/useAnalyticsHelper';
import { GTM4_ANALYTICS_CONSTANTS as GTM_ANALYTICS_CONSTANTS, SUBSCRIPTION_TYPE } from '../../constants';
import {
  EcommerceEvent,
  EcommerceItems,
  EcommercePrice,
  MarketingConsent,
  PurchaseEvent,
  UserStatus,
  UserStatusEcommerceEvent,
  ProductEventItem
} from './types';
import { useAuth0 } from '../Auth0/Auth0';

export const pushAsEcommerceToDataLayer = (eventData: EcommerceEvent): boolean => {
  pushToDataLayer({ ecommerce: null });
  const eventCopy = JSON.parse(JSON.stringify(eventData));
  const { event } = eventCopy;
  delete eventCopy.event;
  pushToDataLayer({
    event,
    ecommerce: {
      ...eventCopy
    }
  });
  return true;
};

const normaliseRecurringInterval = (interval?: string) => {
  if ('ANNUAL' === interval) {
    return 'annually';
  }
  return 'monthly';
}

const mapOrderItemToEcommerceItem = (
  product: OrderItem,
  isTrial: boolean,
  overrides?: { coupon?: string; discount?: number }
): EcommerceItems => ({
  item_id: product.productId,
  item_name: `${product.productFamily} ${product.productTier}`.toLowerCase(),
  affiliation: 'web sale',
  coupon: overrides?.coupon ?? (product.deal?.promoCode || ''),
  discount: overrides?.discount ?? (isTrial ? 0 : product.discountAmount),
  item_brand: 'myob',
  item_category: product.productFamily?.toLowerCase(),
  item_free_period: product.deal?.freePeriod,
  item_payment_state: isTrial ? 'without payment' : 'with payment',
  item_recurring_frequency: normaliseRecurringInterval(product.deal?.recurringInterval),
  price: isTrial ? 0 : Number(product.priceWithoutTax),
  quantity: 1
});

export const useGetEcommerceItems = (): EcommerceItems[] => {
  const { subscriptionType, promoCode, addonIds } = useSessionContext();
  const { productData } = useGetProduct();
  const { totalDiscountAmt } = useGetEcommercePrice();

  const isTrial = subscriptionType === SUBSCRIPTION_TYPE.TRIAL;

  return productData?.items?.flatMap((product) => {
    const baseItem = mapOrderItemToEcommerceItem(product, isTrial, {
      coupon: promoCode,
      discount: totalDiscountAmt
    });

    const addonItems = product.subProducts
      ?.filter((sub) => addonIds?.includes(sub.items[0]?.productId))
      .map((sub) => mapOrderItemToEcommerceItem(sub.items[0], isTrial)) ?? [];

    return [baseItem, ...addonItems];
  }) || [];
};

export const useGetEcommercePrice = (): EcommercePrice => {
  const { productData } = useGetProduct();
  const { subscriptionType, addonIds } = useSessionContext();

  const addOnProductData = (get(productData, 'items[0].subProducts') || [])
    .filter(it => addonIds?.includes(it.items[0].productId));

  const all = [productData, ...addOnProductData];

  if (subscriptionType === SUBSCRIPTION_TYPE.SUBSCRIBE) {
    return {
      totalPriceWithoutTax: sumBy(all, it => it.totalPriceWithoutTax || 0),
      tax: sumBy(all, it => it.tax || 0),
      totalDiscountAmt: sumBy(all, it => it.totalDiscountAmt || 0),
      totalPrice: sumBy(all, it => it.totalPrice || 0),
      currency: productData.currency
    };
  }

  return {
    totalPriceWithoutTax: 0,
    tax: 0,
    totalDiscountAmt: 0,
    totalPrice: 0,
    currency: productData.currency
  };
};

export const useGetProductEventData = (): ProductEventItem => {
  const { productData } = useGetProduct();
  const { productId, promoCode, quoteId } = useSessionContext();
  return {
    product_name: productData.items[0].displayName,
    product_price: productData.totalPrice || 0,
    product_id: productId,
    product_coupon: promoCode,
    product_currency: productData.currency,
    product_category: productData.items[0].productFamily,
    product_quantity: 1,
    product_payment_state: 'with payment',
    product_free_period: productData.items[0].deal?.freePeriod || 0,
    product_quote_reference: quoteId
  }
}

const useBaseEcommerceEvent = () => {
  const ecommerceItems = useGetEcommerceItems();
  const { promoCode } = useSessionContext();
  const { currency, totalPrice } = useGetEcommercePrice();
  return {
    currency,
    value: totalPrice,
    coupon: promoCode,
    items: ecommerceItems
  };
};

export const useGetBeginCheckoutEvent = (): EcommerceEvent => (
  {
    event: GTM_ANALYTICS_CONSTANTS.BEGIN_CHECKOUT,
    ...useBaseEcommerceEvent()
  }
);

export const useGetAddShippingInfoEvent = (isNewUser: boolean): UserStatusEcommerceEvent => (
  {
    user_status: isNewUser ? UserStatus.NEW_USER : UserStatus.EXISTING_USER,
    event: GTM_ANALYTICS_CONSTANTS.ADD_SHIPPING_INFO,
    ...useBaseEcommerceEvent()
  }
);

export const useGetCheckoutSignInEvent = (isNewUser: boolean): UserStatusEcommerceEvent => ({
  ...useGetAddShippingInfoEvent(isNewUser),
  event: GTM_ANALYTICS_CONSTANTS.CHECKOUT_SIGN_IN
});

export const useGetAddPaymentInfoEvent = (): UserStatusEcommerceEvent => {
  const { appState } = useAuth0();
  const { accountDetail } = useSessionContext();
  // TODO: Remove the value from appState when new trial AB testing is finished.
  const sendPromotions = accountDetail?.sendPromotions ?? get(appState, 'clientInformation.sendPromotions');
  const isNewUser = get(appState, 'clientInformation.isNewUser');
  const base = useGetAddShippingInfoEvent(!!isNewUser);
  const { items } = base;
  return {
    ...base,
    event: GTM_ANALYTICS_CONSTANTS.ADD_PAYMENT_INFO,
    items: [...items.map((it) => ({
      ...it,
      item_marketing_consent: sendPromotions ? MarketingConsent.SELECTED : MarketingConsent.NOT_SELECTED
    }))]
  };
};

export const useGetPurchaseEvent = (): PurchaseEvent => {
  const { orderNumber, paymentMethod, accountDetail } = useSessionContext();
  const { appState } = useAuth0();
  // TODO: Remove the value from appState when new trial AB testing is finished.
  const sendPromotions = accountDetail?.sendPromotions ?? get(appState, 'clientInformation.sendPromotions');
  const isNewUser = get(appState, 'clientInformation.isNewUser');

  const { tax } = useGetEcommercePrice();
  const base = useBaseEcommerceEvent();
  const { items } = base;
  return {
    ...useBaseEcommerceEvent(),
    items: [...items.map((it) => ({
      ...it,
      item_marketing_consent: sendPromotions ? MarketingConsent.SELECTED : MarketingConsent.NOT_SELECTED
    }))],
    event: 'purchase_ga4',
    transaction_id: orderNumber,
    tax,
    shipping: 0,
    payment_type: paymentMethod,
    user_status: isNewUser ? UserStatus.NEW_USER : UserStatus.EXISTING_USER
  };
};
